/* eslint-disable max-lines */
import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState
} from 'react';
import { Session } from '@utils/types/editors/sessions.ts';
import {
  DocumentObjectInnerDocument,
  DocumentResult
} from '@utils/types/editors/generic.ts';
import { StartStopWord } from '@utils/types/editors/utils.ts';
import {
  DocumentTranscriptParagraphV1,
  DocumentTranscriptTextV1,
  DocumentTranscriptUtteranceV1,
  DocumentTranscriptV1,
  ScriptixDescendant
} from '@utils/types/editors/transcripts/transcript.ts';
import { Comment } from '@utils/types/comments/comments.ts';
import { useUpdateDocument } from '@utils/api/documents/use-update-document.tsx';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAudio } from '@utils/providers/audio-context.tsx';
import {
  useReportDirty,
  useSplitDirty
} from '@utils/providers/split-dirty-tracker.tsx';
import { Descendant, Editor, Node, Text, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import isEqual from 'fast-deep-equal';
import {
  transcriptDocumentsEqual,
  transcriptParagraphsEqual
} from '../editors/transcript-content-equality.ts';
import {
  clearTranscriptFromLocalStorage,
  saveTranscriptToLocalStorage,
  loadTranscriptFromLocalStorage
} from '@utils/localStorage/transcript-storage.ts';
import { resolveDraftRecovery } from '@utils/localStorage/recovery-decision.ts';
import DraftConflictModal from '@components/atoms/modals/draft-conflict-modal.tsx';
import {
  DraftConflictData,
  forceCloudSourceRequested,
  openCloudVersionInNewTab
} from '@utils/localStorage/draft-conflict.ts';
import {
  TranscriptContext,
  TranscriptContextType
} from './transcript-context.tsx';
import { useGetDocument } from '@utils/api/documents/use-get-document.tsx';

/**
 * Ensures all Slate element nodes have a `children` array.
 */
function normalizeSlateDocument(
  doc: DocumentTranscriptV1 | undefined
): DocumentTranscriptV1 | undefined {
  if (!doc?.document) return doc;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const normalizeNode = (node: any, isUtterance = false): any => {
    if (!node) return node;
    if ((node.type || isUtterance) && !Array.isArray(node.children)) {
      return { ...node, children: [{ text: node.text || '' }] };
    }
    if (Array.isArray(node.children)) {
      return {
        ...node,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        children: node.children.map((child: any) => normalizeNode(child))
      };
    }
    return node;
  };
  return {
    ...doc,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    document: doc.document.map((utterance: any) =>
      normalizeNode(utterance, true)
    )
  };
}

// ---- Reducer ----

type State = {
  session?: Session;
  document?: DocumentTranscriptV1;
  documentResult?: DocumentResult;
  search: string;
  caseSensitive: boolean;
  wholeWord: boolean;
  currentMatch: DocumentTranscriptTextV1 | null;
  startStopWord?: StartStopWord;
  popoverOpen?: boolean;
  popoverPosition?: { x: number; y: number };
  showInfo: boolean;
  clickedComment?: Comment | null;
  showJumpButton: 'up' | 'down' | false;
  speakers: string[];
  isDirty?: boolean;
  matches?: DocumentTranscriptTextV1[];
  undoStack: DocumentTranscriptV1[];
  redoStack: DocumentTranscriptV1[];
  saveStatus: 'unsaved' | 'saved-locally' | 'synced' | 'saving';
  lastLocalSave: number | null;
  lastServerSync: number | null;
  documentVersion: number;
  selectedParagraphs: number[];
};

type Action = { type: string; payload: unknown };

const initialState: State = {
  session: undefined,
  document: undefined,
  search: '',
  startStopWord: undefined,
  popoverOpen: false,
  popoverPosition: undefined,
  showInfo: false,
  clickedComment: null,
  showJumpButton: false,
  documentResult: undefined,
  caseSensitive: false,
  wholeWord: false,
  currentMatch: null,
  speakers: [],
  isDirty: false,
  matches: [],
  undoStack: [],
  redoStack: [],
  saveStatus: 'synced',
  lastLocalSave: null,
  lastServerSync: null,
  documentVersion: 0,
  selectedParagraphs: []
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_SESSION':
      return { ...state, session: action.payload as Session };
    case 'SET_DOCUMENT':
      return { ...state, document: action.payload as DocumentTranscriptV1 };
    case 'SET_SEARCH':
      return { ...state, search: action.payload as string };
    case 'SET_START_STOP_WORD':
      return { ...state, startStopWord: action.payload as StartStopWord };
    case 'SET_POPOVER_OPEN':
      return { ...state, popoverOpen: action.payload as boolean };
    case 'SET_POPOVER_POSITION':
      return {
        ...state,
        popoverPosition: action.payload as { x: number; y: number }
      };
    case 'SET_SHOW_INFO':
      return { ...state, showInfo: action.payload as boolean };
    case 'SET_CLICKED_COMMENT':
      return { ...state, clickedComment: action.payload as Comment | null };
    case 'SET_JUMP_BUTTON':
      return {
        ...state,
        showJumpButton: action.payload as 'up' | 'down' | false
      };
    case 'SET_DOCUMENT_RESULT':
      return { ...state, documentResult: action.payload as DocumentResult };
    case 'SET_CASE_SENSITIVE':
      return { ...state, caseSensitive: action.payload as boolean };
    case 'SET_WHOLE_WORD':
      return { ...state, wholeWord: action.payload as boolean };
    case 'SET_CURRENT_MATCH':
      return {
        ...state,
        currentMatch: action.payload as DocumentTranscriptTextV1 | null
      };
    case 'SET_SPEAKERS':
      return { ...state, speakers: action.payload as string[] };
    case 'SET_IS_DIRTY':
      return { ...state, isDirty: action.payload as boolean };
    case 'SET_MATCHES':
      return {
        ...state,
        matches: action.payload as DocumentTranscriptTextV1[]
      };
    case 'SET_UNDO_STACK':
      return { ...state, undoStack: action.payload as DocumentTranscriptV1[] };
    case 'SET_REDO_STACK':
      return { ...state, redoStack: action.payload as DocumentTranscriptV1[] };
    case 'SET_SAVE_STATUS':
      return {
        ...state,
        saveStatus: action.payload as
          | 'unsaved'
          | 'saved-locally'
          | 'synced'
          | 'saving'
      };
    case 'SET_LAST_LOCAL_SAVE':
      return { ...state, lastLocalSave: action.payload as number | null };
    case 'SET_LAST_SERVER_SYNC':
      return { ...state, lastServerSync: action.payload as number | null };
    case 'INCREMENT_DOCUMENT_VERSION':
      return { ...state, documentVersion: state.documentVersion + 1 };
    case 'SET_SELECTED_PARAGRAPHS':
      return { ...state, selectedParagraphs: action.payload as number[] };
    default:
      return state;
  }
}

// ---- Props ----

interface TranslationTranscriptProviderProps {
  children: React.ReactNode;
  session: Session;
  sessionId: string;
  documentId: string | null;
  /** Called when an editor in this pane gets focus — so the source toolbar can target it */
  onEditorFocus?: (editor: ReactEditor | null) => void;
  /** Forward startStopWord to source context so toolbar styling targets correct text */
  onStartStopWordChange?: (word: StartStopWord) => void;
}

// ---- Provider ----

const MAX_HISTORY = 50;

export function TranslationTranscriptProvider({
  children,
  session,
  sessionId,
  documentId,
  onEditorFocus,
  onStartStopWordChange
}: TranslationTranscriptProviderProps) {
  const { t } = useTranslation();
  useAudio(); // Ensure AudioProvider is available for child components

  // Fetch document
  const { data: fetchedDocument } = useGetDocument({
    sessionId,
    documentId: documentId ?? undefined
  });

  const hasRecoveredData = useRef(false);
  const initialStateRef = useRef<State | null>(null);
  // Holds the unresolved local-vs-cloud conflict, if any (see DraftConflictModal).
  const initialConflictRef =
    useRef<DraftConflictData<DocumentTranscriptV1> | null>(null);

  if (initialStateRef.current === null) {
    const rawDoc = fetchedDocument?.result?.document as DocumentTranscriptV1;
    const apiDocument = normalizeSlateDocument(rawDoc);

    let recoveredData: {
      document: DocumentTranscriptV1;
      undoStack: DocumentTranscriptV1[];
      redoStack: DocumentTranscriptV1[];
      isDirty: boolean;
      recoveryTimestamp: number;
    } | null = null;

    // The "view cloud version" tab bypasses local recovery entirely.
    const forceCloud = forceCloudSourceRequested();

    if (sessionId && documentId && !forceCloud) {
      const localData = loadTranscriptFromLocalStorage(sessionId, documentId);
      if (localData && localData.document) {
        let serverTimestamp = 0;
        if (fetchedDocument?.result?.last_modified) {
          const parsedTime = new Date(
            fetchedDocument.result.last_modified
          ).getTime();
          serverTimestamp = isNaN(parsedTime) ? 0 : parsedTime;
        }
        const localTimestamp = localData.lastLocalSave;
        const documentsDiffer = !transcriptDocumentsEqual(
          localData.document,
          apiDocument
        );
        // Reconcile the local draft with the server copy:
        //  - 'recover'  → genuine unsynced edits, server not ahead: load local.
        //  - 'server'   → local is stale/clean: server is authoritative.
        //  - 'conflict' → both advanced independently: ask the user (block the
        //    editor behind DraftConflictModal). Never silently drop either side
        //    (data-loss guard, Smartcomtv incident 2026-06).
        const decision = resolveDraftRecovery({
          localIsDirty: localData.isDirty,
          localLastSave: localTimestamp,
          localLastServerSync: localData.lastServerSync,
          serverLastModified: serverTimestamp,
          documentsDiffer
        });
        const serverDoc = fetchedDocument?.result
          ?.document as DocumentTranscriptV1;
        if (decision === 'recover') {
          const isActuallyDirty = !transcriptDocumentsEqual(
            localData.document,
            serverDoc
          );
          recoveredData = {
            document: localData.document,
            undoStack: localData.undoStack || [],
            redoStack: localData.redoStack || [],
            isDirty: isActuallyDirty,
            recoveryTimestamp: localTimestamp || Date.now()
          };
        } else if (decision === 'conflict') {
          // Keep the server copy as the safe live baseline; capture the local
          // draft so the modal can offer it. Nothing is applied until the user
          // chooses, so no autosave can clobber either version.
          initialConflictRef.current = {
            localDocument: localData.document,
            localUndo: localData.undoStack || [],
            localRedo: localData.redoStack || [],
            localTimestamp: localTimestamp || 0,
            serverTimestamp,
            localCount: localData.document.document?.length,
            serverCount: serverDoc?.document?.length
          };
        }
      }
    }

    const finalDocument = recoveredData?.document ?? apiDocument;
    hasRecoveredData.current = !!recoveredData;

    const speakers = finalDocument?.document
      ? Array.from(
          new Set(
            finalDocument.document
              .filter(p => p.speaker && p.speaker.length > 0)
              .map(p => p.speaker || '')
          )
        )
      : [];

    let saveStatus: 'unsaved' | 'saved-locally' | 'synced' = 'synced';
    if (recoveredData) {
      saveStatus = recoveredData.recoveryTimestamp
        ? 'saved-locally'
        : 'unsaved';
    }

    initialStateRef.current = {
      ...initialState,
      session,
      document: finalDocument,
      documentResult: fetchedDocument,
      undoStack: recoveredData?.undoStack ?? [],
      redoStack: recoveredData?.redoStack ?? [],
      isDirty: recoveredData?.isDirty ?? false,
      saveStatus,
      lastLocalSave: recoveredData?.recoveryTimestamp ?? null,
      speakers
    };
  }

  const [state, dispatch] = useReducer(reducer, initialStateRef.current);

  // Unresolved local-vs-cloud conflict. While set, the editor is blocked behind
  // DraftConflictModal so neither version can be silently overwritten.
  const [conflict, setConflict] =
    useState<DraftConflictData<DocumentTranscriptV1> | null>(
      () => initialConflictRef.current
    );

  useEffect(() => {
    if (session && session !== state.session) {
      dispatch({ type: 'SET_SESSION', payload: session });
    }
  }, [session]);

  // Sync from API after initial mount
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (!fetchedDocument) return;
    // A local draft can render before the query resolves. Save still needs
    // the server metadata, even while the recovered body stays untouched.
    dispatch({ type: 'SET_DOCUMENT_RESULT', payload: fetchedDocument });
    const normalized = normalizeSlateDocument(
      fetchedDocument.result.document as DocumentTranscriptV1
    );
    // Recovery runs before a cold query has a body to compare with. Once
    // it arrives, a matching local draft is already synced, not a new edit.
    if (
      hasRecoveredData.current &&
      transcriptDocumentsEqual(documentRef.current, normalized)
    ) {
      dispatch({ type: 'SET_IS_DIRTY', payload: false });
      dispatch({ type: 'SET_SAVE_STATUS', payload: 'synced' });
      const modified = new Date(fetchedDocument.result.last_modified).getTime();
      const syncedAt = Number.isFinite(modified) ? modified : Date.now();
      dispatch({ type: 'SET_LAST_SERVER_SYNC', payload: syncedAt });
      saveTranscriptToLocalStorage(sessionId, fetchedDocument.result.id, {
        document: documentRef.current,
        isDirty: false,
        lastServerSync: syncedAt
      });
    }
    // Once hydrated, cache updates must not replace the live editor body.
    // A user can keep typing while a previous Save updates that cache.
    if (hasRecoveredData.current || documentRef.current) return;
    dispatch({ type: 'SET_DOCUMENT', payload: normalized });
    documentRef.current = normalized;
  }, [fetchedDocument]);

  const { mutate: updateDocumentMutation, isLoading: saveDocumentLoading } =
    useUpdateDocument();

  const currentEditorRef = useRef<ReactEditor | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const documentRef = useRef<DocumentTranscriptV1 | undefined>(
    initialStateRef.current.document
  );
  const undoStackRef = useRef<DocumentTranscriptV1[]>(
    initialStateRef.current.undoStack
  );
  const redoStackRef = useRef<DocumentTranscriptV1[]>(
    initialStateRef.current.redoStack
  );
  const isSavingRef = useRef(false);
  const pendingEditTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastEditTimeRef = useRef<number>(0);
  // Shell shares this via `useSplitDirty` so the "Unsaved changes"
  // prompt (driven by `anyDirty`) can be cleared the moment the save
  // completes. The regular `useReportDirty` effect ALSO fires on
  // `state.isDirty` change, but it runs after the next render — long
  // enough for a quick "back" click to still see the pre-save true
  // flag and trigger the prompt even though we're already clean.
  const { reportDirty: reportSplitDirty } = useSplitDirty();
  const reportSplitDirtyRef = useRef(reportSplitDirty);
  reportSplitDirtyRef.current = reportSplitDirty;

  useEffect(() => {
    undoStackRef.current = state.undoStack;
    redoStackRef.current = state.redoStack;
  }, [state.undoStack, state.redoStack]);

  // ---- Helpers ----

  const setSaveStatus = useCallback(
    (status: 'unsaved' | 'saved-locally' | 'synced' | 'saving') => {
      dispatch({ type: 'SET_SAVE_STATUS', payload: status });
    },
    []
  );
  const setLastLocalSave = useCallback((ts: number | null) => {
    dispatch({ type: 'SET_LAST_LOCAL_SAVE', payload: ts });
  }, []);
  const setLastServerSync = useCallback((ts: number | null) => {
    dispatch({ type: 'SET_LAST_SERVER_SYNC', payload: ts });
  }, []);
  const setIsDirty = useCallback((d: boolean) => {
    dispatch({ type: 'SET_IS_DIRTY', payload: d });
  }, []);

  // Derive speakers from document
  const deriveSpeakers = (doc?: DocumentTranscriptV1) => {
    if (!doc) return [];
    return Array.from(
      new Set(
        doc.document
          .map(u => (u.speaker ?? '').trim())
          .filter(s => s.length > 0)
      )
    );
  };

  useEffect(() => {
    if (state.document) {
      dispatch({
        type: 'SET_SPEAKERS',
        payload: deriveSpeakers(state.document)
      });
    }
  }, [state.document]);

  // ---- Undo / Redo ----

  const addVersionChange = useCallback(() => {
    if (!state.document) return;
    const nextUndo = [...state.undoStack, state.document];
    const trimmedUndo =
      nextUndo.length > MAX_HISTORY
        ? nextUndo.slice(nextUndo.length - MAX_HISTORY)
        : nextUndo;
    dispatch({ type: 'SET_UNDO_STACK', payload: trimmedUndo });
    dispatch({ type: 'SET_REDO_STACK', payload: [] });
    dispatch({ type: 'SET_IS_DIRTY', payload: true });
    if (state.saveStatus !== 'saving') setSaveStatus('unsaved');
  }, [state.document, state.undoStack, state.saveStatus, setSaveStatus]);

  const undo = useCallback(() => {
    if (state.undoStack.length === 0) return;
    const undoStackCopy = [...state.undoStack];
    const lastState = undoStackCopy.pop();
    if (lastState) {
      const newRedoStack = state.document
        ? [...state.redoStack, state.document]
        : state.redoStack;
      if (state.document) {
        dispatch({ type: 'SET_REDO_STACK', payload: newRedoStack });
      }
      dispatch({ type: 'SET_UNDO_STACK', payload: undoStackCopy });
      dispatch({ type: 'SET_DOCUMENT', payload: lastState });
      dispatch({ type: 'INCREMENT_DOCUMENT_VERSION', payload: null });
      documentRef.current = lastState;
      if (sessionId && state.documentResult) {
        saveTranscriptToLocalStorage(
          sessionId,
          state.documentResult.result.id,
          {
            document: lastState,
            undoStack: undoStackCopy,
            redoStack: newRedoStack,
            isDirty: true
          }
        );
        setSaveStatus('saved-locally');
        setLastLocalSave(Date.now());
      }
    }
  }, [
    state.undoStack,
    state.redoStack,
    state.document,
    state.documentResult,
    sessionId,
    setSaveStatus,
    setLastLocalSave
  ]);

  const redo = useCallback(() => {
    if (state.redoStack.length === 0) return;
    const redoStackCopy = [...state.redoStack];
    const nextState = redoStackCopy.pop();
    if (nextState) {
      const newUndoStack = state.document
        ? [...state.undoStack, state.document]
        : state.undoStack;
      if (state.document) {
        dispatch({ type: 'SET_UNDO_STACK', payload: newUndoStack });
      }
      dispatch({ type: 'SET_REDO_STACK', payload: redoStackCopy });
      dispatch({ type: 'SET_DOCUMENT', payload: nextState });
      dispatch({ type: 'INCREMENT_DOCUMENT_VERSION', payload: null });
      documentRef.current = nextState;
      if (sessionId && state.documentResult) {
        saveTranscriptToLocalStorage(
          sessionId,
          state.documentResult.result.id,
          {
            document: nextState,
            undoStack: newUndoStack,
            redoStack: redoStackCopy,
            isDirty: true
          }
        );
        setSaveStatus('saved-locally');
        setLastLocalSave(Date.now());
      }
    }
  }, [
    state.redoStack,
    state.undoStack,
    state.document,
    state.documentResult,
    sessionId,
    setSaveStatus,
    setLastLocalSave
  ]);

  // ---- Editor update paragraph ----

  const editorUpdateParagraph = useCallback(
    (children: Descendant[]) => {
      // Read from `documentRef.current` instead of `state.document`:
      // Replace All iterates every mounted editor synchronously and
      // each editor's Slate `onChange` lands here. React batches those
      // dispatches, so each call sees the same STALE `state.document`
      // from closure and only the last dispatch wins — paragraphs
      // 1..N-1 silently lose their replacement, and the server PUT
      // (also sourced from state.document) misses them too. The ref
      // is mutated synchronously below, so each follower reads the
      // previous call's result and compounds correctly.
      const sourceDoc = documentRef.current ?? state.document;
      if (!sourceDoc || !children || children.length === 0) return;
      const childrenDocument = (
        children.filter(
          (child: Descendant) =>
            (child as ScriptixDescendant).type !== 'speaker'
        ) as Array<DocumentTranscriptUtteranceV1>
      )?.[0];
      if (!childrenDocument) return;
      const paragraph = sourceDoc.document.find(
        p =>
          p.start === childrenDocument.start && p.stop === childrenDocument.stop
      );
      if (!paragraph) return;
      const index = sourceDoc.document.indexOf(paragraph);
      if (index === -1) return;
      const childrenChanged =
        paragraph.children.length !== childrenDocument.children.length;
      const hasChanged =
        childrenChanged ||
        !transcriptParagraphsEqual(
          paragraph.children,
          childrenDocument.children
        );
      if (!hasChanged) return;

      const now = Date.now();
      const timeSinceLastEdit = now - lastEditTimeRef.current;
      if (timeSinceLastEdit > 500) addVersionChange();
      lastEditTimeRef.current = now;

      const updatedDocument = [...sourceDoc.document];
      updatedDocument[index] = {
        ...paragraph,
        children: childrenDocument.children
      };
      const newDoc = { ...sourceDoc, document: updatedDocument };
      documentRef.current = newDoc;
      dispatch({ type: 'SET_DOCUMENT', payload: newDoc });
      setIsDirty(true);

      if (pendingEditTimerRef.current)
        clearTimeout(pendingEditTimerRef.current);
      pendingEditTimerRef.current = setTimeout(() => {
        if (timeSinceLastEdit <= 500 && state.document) {
          const nextUndo = [...state.undoStack, state.document];
          const trimmedUndo =
            nextUndo.length > MAX_HISTORY
              ? nextUndo.slice(nextUndo.length - MAX_HISTORY)
              : nextUndo;
          dispatch({ type: 'SET_UNDO_STACK', payload: trimmedUndo });
          dispatch({ type: 'SET_REDO_STACK', payload: [] });
        }
      }, 500);
    },
    [state.document, state.undoStack, addVersionChange, setIsDirty]
  );

  // ---- Utterance operations ----

  const addUtterance = useCallback(
    (isFirstIndex?: boolean) => {
      if (!state.document) return;
      addVersionChange();
      const updatedDocument = [...state.document.document];
      const currentEditor = currentEditorRef.current;

      if (isFirstIndex && updatedDocument.length > 0) {
        const firstUtterance = updatedDocument[0];
        const newUtterance: DocumentTranscriptUtteranceV1 = {
          type: 'utterance',
          speaker: '',
          start: 0,
          stop: firstUtterance.start,
          children: [
            {
              type: 'paragraph',
              start: 0,
              stop: firstUtterance.start,
              children: [{ text: ' ', start: 0, stop: firstUtterance.start }]
            }
          ]
        };
        updatedDocument.splice(0, 0, newUtterance);
        const newDoc = { ...state.document, document: updatedDocument };
        documentRef.current = newDoc;
        dispatch({ type: 'SET_DOCUMENT', payload: newDoc });
        setIsDirty(true);
        return;
      }

      if (!currentEditor) return;
      const { selection } = currentEditor;
      if (!selection) return;
      const [selectedNode] = Editor.node(currentEditor, selection.focus.path);
      if (
        !selectedNode ||
        !('start' in selectedNode) ||
        !('stop' in selectedNode)
      )
        return;

      const currentUtteranceIndex = updatedDocument.findIndex(
        utterance =>
          (utterance.start || 0) <= Number(selectedNode.start) &&
          (utterance.stop || 0) >= Number(selectedNode.stop)
      );

      if (currentUtteranceIndex !== -1) {
        const currentUtterance = updatedDocument[currentUtteranceIndex];
        const nextUtterance = updatedDocument[currentUtteranceIndex + 1];
        const newStart = Number(selectedNode.stop);
        const newStop = nextUtterance?.start ?? Number(selectedNode.stop);
        const newUtterance: DocumentTranscriptUtteranceV1 = {
          type: 'utterance',
          speaker: currentUtterance.speaker,
          start: newStart,
          stop: newStop,
          children: [
            {
              type: 'paragraph',
              start: newStart,
              stop: newStop,
              children: [{ text: ' ', start: newStart, stop: newStop }]
            }
          ]
        };
        updatedDocument.splice(currentUtteranceIndex + 1, 0, newUtterance);
        const newDoc = { ...state.document, document: updatedDocument };
        documentRef.current = newDoc;
        dispatch({ type: 'SET_DOCUMENT', payload: newDoc });
        setIsDirty(true);
      }
    },
    [state.document, addVersionChange, setIsDirty]
  );

  const deleteUtterance = useCallback(
    (index: number) => {
      if (!state.document) return;
      addVersionChange();
      const updatedDocument = [...state.document.document];
      updatedDocument.splice(index, 1);
      const newDoc = { ...state.document, document: updatedDocument };
      documentRef.current = newDoc;
      dispatch({ type: 'SET_DOCUMENT', payload: newDoc });
      setIsDirty(true);
    },
    [state.document, addVersionChange, setIsDirty]
  );

  const splitUtterance = useCallback(
    (index: number) => {
      if (!state.document) return;
      const currentEditor = currentEditorRef.current;
      if (!currentEditor) return;
      const { selection } = currentEditor;
      if (!selection) return;
      const [selectedNode] = Editor.node(currentEditor, selection.focus.path);
      if (
        !selectedNode ||
        !('start' in selectedNode) ||
        !('stop' in selectedNode)
      )
        return;
      addVersionChange();
      const splitStartTime = Number(selectedNode.start);
      const updatedDocument = [...state.document.document];
      const utteranceToSplit = updatedDocument[index];
      const beforeChildren = utteranceToSplit.children
        .map(child => ({
          ...child,
          children: child.children.filter(
            textNode =>
              textNode.start !== undefined &&
              textNode.stop !== undefined &&
              textNode.stop <= splitStartTime
          ),
          start: child.children[0]?.start,
          stop: splitStartTime
        }))
        .filter(child => child.children.length > 0);
      const afterChildren = utteranceToSplit.children
        .map(child => ({
          ...child,
          children: child.children.filter(
            textNode =>
              textNode.start !== undefined &&
              textNode.stop !== undefined &&
              textNode.start >= splitStartTime
          ),
          start: splitStartTime,
          stop: child.children[child.children.length - 1]?.stop
        }))
        .filter(child => child.children.length > 0);
      if (beforeChildren.length > 0 && afterChildren.length > 0) {
        const beforeSplit = {
          ...utteranceToSplit,
          stop: splitStartTime,
          children: beforeChildren
        };
        const afterSplit = {
          ...utteranceToSplit,
          start: splitStartTime,
          children: afterChildren
        };
        updatedDocument.splice(index, 1, beforeSplit, afterSplit);
        const newDoc = { ...state.document, document: updatedDocument };
        documentRef.current = newDoc;
        dispatch({ type: 'SET_DOCUMENT', payload: newDoc });
        setIsDirty(true);
      }
    },
    [state.document, addVersionChange, setIsDirty]
  );

  const mergeUtterances = useCallback(
    (indices: number[]) => {
      if (!state.document || indices.length < 2) return;
      const sortedIndices = [...indices].sort((a, b) => a - b);
      addVersionChange();
      const updatedDocument = [...state.document.document];
      const utterancesToMerge = sortedIndices.map(i => updatedDocument[i]);
      const firstUtterance = utterancesToMerge[0];
      const lastUtterance = utterancesToMerge[utterancesToMerge.length - 1];
      const mergedChildren: DocumentTranscriptParagraphV1[] = [];
      for (const utterance of utterancesToMerge) {
        mergedChildren.push(...utterance.children);
      }
      const mergedUtterance: DocumentTranscriptUtteranceV1 = {
        type: 'utterance',
        speaker: firstUtterance.speaker,
        start: firstUtterance.start,
        stop: lastUtterance.stop,
        children: mergedChildren
      };
      for (let i = sortedIndices.length - 1; i >= 0; i--) {
        updatedDocument.splice(sortedIndices[i], 1);
      }
      updatedDocument.splice(sortedIndices[0], 0, mergedUtterance);
      const newDoc = { ...state.document, document: updatedDocument };
      documentRef.current = newDoc;
      dispatch({ type: 'SET_DOCUMENT', payload: newDoc });
      dispatch({ type: 'SET_SELECTED_PARAGRAPHS', payload: [] });
      setIsDirty(true);
      toast.success(t('paragraphs-merged-successfully'));
    },
    [state.document, addVersionChange, setIsDirty, t]
  );

  // ---- Speaker operations ----

  const addSpeaker = useCallback(
    (nextName: string, element: ScriptixDescendant) => {
      if (!state.document) return;
      const name = (nextName ?? '').trim();
      if (!name) return;
      addVersionChange();
      const updated = state.document.document.map(utt => {
        const sameUtterance =
          utt.start === element.start && utt.stop === element.stop;
        return sameUtterance ? { ...utt, speaker: name } : utt;
      });
      const nextDoc: DocumentTranscriptV1 = {
        ...state.document,
        document: updated
      };
      documentRef.current = nextDoc;
      dispatch({ type: 'SET_DOCUMENT', payload: nextDoc });
      dispatch({ type: 'SET_SPEAKERS', payload: deriveSpeakers(nextDoc) });
      setIsDirty(true);
    },
    [state.document, addVersionChange, setIsDirty]
  );

  const replaceSpeakers = useCallback(
    (oldSpeaker: string, newSpeaker: string) => {
      if (!state.document) return;
      addVersionChange();
      const updated = state.document.document.map(utt =>
        utt.speaker === oldSpeaker ? { ...utt, speaker: newSpeaker } : utt
      );
      const nextDoc: DocumentTranscriptV1 = {
        ...state.document,
        document: updated
      };
      documentRef.current = nextDoc;
      dispatch({ type: 'SET_DOCUMENT', payload: nextDoc });
      dispatch({ type: 'SET_SPEAKERS', payload: deriveSpeakers(nextDoc) });
      setIsDirty(true);
    },
    [state.document, addVersionChange, setIsDirty]
  );

  // ---- Style ----

  const applyStyle = useCallback(
    (
      style:
        | 'bold'
        | 'italic'
        | 'underlined'
        | 'color'
        | 'strike'
        | 'mark'
        | 'unmark',
      colorValue?: string | null,
      editor?: ReactEditor
    ) => {
      // Mirror the source-pane `applyStyle` — walks the translation
      // editor's text nodes filtered by `state.startStopWord` and
      // applies the style prop via `Transforms.setNodes`. Previously
      // this was a no-op, which silently broke every toolbar button
      // (Bold, Italic, Underline, Strike, Highlight, Remove-highlight)
      // in the translation pane once the split view added a toolbar.
      const target = editor ?? currentEditorRef.current;
      if (!target || !state.startStopWord) return;
      addVersionChange();
      const { startStopWord } = state;
      Editor.withoutNormalizing(target, () => {
        for (const [node, path] of Editor.nodes(target, {
          at: [],
          match: (n, nodePath) => {
            if (!Text.isText(n)) return false;
            const t = n as DocumentTranscriptTextV1;
            if (typeof t.start !== 'number' || typeof t.stop !== 'number')
              return false;
            if (
              !(
                (t.start >= startStopWord!.start || t.start === 0) &&
                t.stop <= startStopWord!.stop
              )
            )
              return false;
            if (nodePath.length > 0) {
              const utterance = Node.get(target, [nodePath[0]]) as {
                type?: string;
              };
              if (utterance.type === 'speaker') return false;
            }
            return true;
          }
        })) {
          const textNode = node as DocumentTranscriptTextV1;
          const props: Partial<DocumentTranscriptTextV1> = {};
          if (style === 'unmark') {
            props.mark = false;
          } else if (style === 'mark') {
            props.mark = colorValue ? true : !textNode.mark;
            if (colorValue) props.color = colorValue;
          } else if (style === 'color' && colorValue) {
            props.color = colorValue;
          } else {
            (props as Record<string, boolean>)[style] =
              !textNode[style as keyof DocumentTranscriptTextV1];
          }
          Transforms.setNodes(target, props as Partial<Descendant>, {
            at: path
          });
        }
      });
      setIsDirty(true);
    },
    [state, addVersionChange, setIsDirty]
  );

  // ---- Save ----

  const saveDocument = useCallback(
    (userAction?: boolean) => {
      const currentDocument = documentRef.current || state.document;
      if (!currentDocument || !sessionId || !state.documentResult) return;
      isSavingRef.current = true;
      setSaveStatus('saving');
      if (userAction) toast(t('saving-document'));
      updateDocumentMutation(
        {
          sessionId,
          documentId: state.documentResult.result.id!,
          document: currentDocument as DocumentObjectInnerDocument,
          plain_document_changed: !!userAction,
          dontInvalidateQueries: true
        },
        {
          onSuccess: () => {
            isSavingRef.current = false;
            const now = Date.now();
            const savedCurrentVersion = isEqual(
              documentRef.current,
              currentDocument
            );
            setIsDirty(!savedCurrentVersion);
            setSaveStatus(savedCurrentVersion ? 'synced' : 'unsaved');
            setLastServerSync(now);
            if (pendingEditTimerRef.current) {
              clearTimeout(pendingEditTimerRef.current);
              pendingEditTimerRef.current = null;
            }
            // Flush the split-dirty tracker synchronously so the
            // shell's `usePrompt({ isDirty || splitAnyDirty })` sees a
            // clean state before the user can click "back". The
            // reactive `useReportDirty` path also updates it, but one
            // render later — enough window for the leave-prompt to
            // fire even on a successful save.
            if (documentId)
              reportSplitDirtyRef.current(documentId, !savedCurrentVersion);
            if (userAction) toast.success(t('document-saved-successfully'));
            if (state.documentResult && currentDocument) {
              saveTranscriptToLocalStorage(
                sessionId,
                state.documentResult.result.id,
                {
                  document: documentRef.current ?? currentDocument,
                  undoStack: undoStackRef.current,
                  redoStack: redoStackRef.current,
                  isDirty: !savedCurrentVersion,
                  lastLocalSave: savedCurrentVersion
                    ? (state.lastLocalSave ?? undefined)
                    : now,
                  lastServerSync: now,
                  filename: state.documentResult.result.filename,
                  timecodeOffset: state.documentResult.result.timecode_offset
                }
              );
            }
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onError: (error: any) => {
            isSavingRef.current = false;
            setSaveStatus(state.lastLocalSave ? 'saved-locally' : 'unsaved');
            if (userAction) {
              const errorMessage =
                error?.response?.data?.error_description ||
                error?.response?.data?.detail ||
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                t('failed-to-save-document');
              toast.error(errorMessage);
            }
          }
        }
      );
    },
    [
      state.document,
      state.documentResult,
      state.lastLocalSave,
      sessionId,
      updateDocumentMutation,
      setSaveStatus,
      setIsDirty,
      setLastServerSync,
      t
    ]
  );

  const silentSave = useCallback(
    (updatedDocument?: DocumentTranscriptV1) => {
      if (!state.isDirty && !updatedDocument) return;
      if (isSavingRef.current) return;
      if (!state.documentResult || !sessionId) return;
      if (state.documentResult.result.finished) return;
      const docToSave =
        updatedDocument || documentRef.current || state.document;
      if (!docToSave) return;
      isSavingRef.current = true;
      setSaveStatus('saving');
      updateDocumentMutation(
        {
          sessionId,
          documentId: state.documentResult.result.id!,
          document: docToSave as DocumentObjectInnerDocument,
          dontInvalidateQueries: true
        },
        {
          onSuccess: () => {
            isSavingRef.current = false;
            const now = Date.now();
            const savedCurrentVersion = isEqual(documentRef.current, docToSave);
            setIsDirty(!savedCurrentVersion);
            setSaveStatus(savedCurrentVersion ? 'synced' : 'unsaved');
            setLastServerSync(now);
            if (pendingEditTimerRef.current) {
              clearTimeout(pendingEditTimerRef.current);
              pendingEditTimerRef.current = null;
            }
            if (state.documentResult && docToSave) {
              saveTranscriptToLocalStorage(
                sessionId,
                state.documentResult.result.id,
                {
                  document: documentRef.current ?? docToSave,
                  undoStack: undoStackRef.current,
                  redoStack: redoStackRef.current,
                  isDirty: !savedCurrentVersion,
                  lastLocalSave: savedCurrentVersion
                    ? (state.lastLocalSave ?? undefined)
                    : now,
                  lastServerSync: now,
                  filename: state.documentResult.result.filename,
                  timecodeOffset: state.documentResult.result.timecode_offset
                }
              );
            }
          },
          onError: () => {
            isSavingRef.current = false;
            if (state.saveStatus === 'saving') {
              setSaveStatus(state.lastLocalSave ? 'saved-locally' : 'unsaved');
            }
          }
        }
      );
    },
    [
      state.isDirty,
      state.document,
      state.documentResult,
      state.lastLocalSave,
      state.saveStatus,
      sessionId,
      updateDocumentMutation,
      setSaveStatus,
      setIsDirty,
      setLastServerSync
    ]
  );

  // ---- Simple setters ----

  const setSearch = useCallback((q: string) => {
    dispatch({ type: 'SET_SEARCH', payload: q });
  }, []);
  const setCaseSensitive = useCallback((v: boolean) => {
    dispatch({ type: 'SET_CASE_SENSITIVE', payload: v });
  }, []);
  const setWholeWord = useCallback((v: boolean) => {
    dispatch({ type: 'SET_WHOLE_WORD', payload: v });
  }, []);
  const setCurrentMatch = useCallback((m: DocumentTranscriptTextV1 | null) => {
    dispatch({ type: 'SET_CURRENT_MATCH', payload: m });
  }, []);
  const setStartStopWord = useCallback(
    (w: StartStopWord) => {
      dispatch({ type: 'SET_START_STOP_WORD', payload: w });
      // Also notify source context so toolbar styling targets correct text
      onStartStopWordChange?.(w);
    },
    [onStartStopWordChange]
  );
  const setPopoverOpen = useCallback((o: boolean) => {
    dispatch({ type: 'SET_POPOVER_OPEN', payload: o });
  }, []);
  const setPopoverPosition = useCallback((p: { x: number; y: number }) => {
    dispatch({ type: 'SET_POPOVER_POSITION', payload: p });
  }, []);
  const setShowInfo = useCallback((v: boolean) => {
    dispatch({ type: 'SET_SHOW_INFO', payload: v });
  }, []);
  const setClickedComment = useCallback((c: Comment | null) => {
    dispatch({ type: 'SET_CLICKED_COMMENT', payload: c });
  }, []);
  const setShowJumpButton = useCallback((d: 'up' | 'down' | false) => {
    dispatch({ type: 'SET_JUMP_BUTTON', payload: d });
  }, []);
  const setEditor = useCallback(
    (e: ReactEditor | null) => {
      currentEditorRef.current = e;
      // Also notify source context so toolbar styling targets this editor
      onEditorFocus?.(e);
    },
    [onEditorFocus]
  );
  const updateDocument = useCallback((doc: DocumentTranscriptV1) => {
    dispatch({ type: 'SET_DOCUMENT', payload: doc });
  }, []);
  const updateDocumentResult = useCallback((dr: DocumentResult) => {
    dispatch({ type: 'SET_DOCUMENT_RESULT', payload: dr });
  }, []);
  const setMatches = useCallback((m: DocumentTranscriptTextV1[]) => {
    dispatch({ type: 'SET_MATCHES', payload: m || [] });
  }, []);
  const focusMatch = useCallback((m: DocumentTranscriptTextV1 | null) => {
    dispatch({ type: 'SET_CURRENT_MATCH', payload: m ?? null });
    if (m && m.start !== undefined && m.stop !== undefined) {
      window.dispatchEvent(
        new CustomEvent('transcript:focusMatch', {
          detail: { start: m.start, stop: m.stop }
        })
      );
    }
  }, []);
  const setSelectedParagraphs = useCallback((indices: number[]) => {
    dispatch({ type: 'SET_SELECTED_PARAGRAPHS', payload: indices });
  }, []);
  const toggleParagraphSelection = useCallback(
    (index: number, multiSelect: boolean) => {
      if (!multiSelect) {
        dispatch({ type: 'SET_SELECTED_PARAGRAPHS', payload: [index] });
      } else {
        const isSelected = state.selectedParagraphs.includes(index);
        dispatch({
          type: 'SET_SELECTED_PARAGRAPHS',
          payload: isSelected
            ? state.selectedParagraphs.filter(i => i !== index)
            : [...state.selectedParagraphs, index].sort((a, b) => a - b)
        });
      }
    },
    [state.selectedParagraphs]
  );
  const clearParagraphSelection = useCallback(() => {
    dispatch({ type: 'SET_SELECTED_PARAGRAPHS', payload: [] });
  }, []);

  const reloadFromServer = useCallback(
    (freshDocument: DocumentResult) => {
      const newDocument = normalizeSlateDocument(
        freshDocument.result.document as DocumentTranscriptV1
      );
      if (sessionId && state.documentResult) {
        clearTranscriptFromLocalStorage(
          sessionId,
          state.documentResult.result.id
        );
      }
      dispatch({ type: 'SET_DOCUMENT', payload: newDocument });
      dispatch({ type: 'SET_DOCUMENT_RESULT', payload: freshDocument });
      documentRef.current = newDocument;
      dispatch({ type: 'SET_UNDO_STACK', payload: [] });
      dispatch({ type: 'SET_REDO_STACK', payload: [] });
      undoStackRef.current = [];
      redoStackRef.current = [];
      dispatch({ type: 'INCREMENT_DOCUMENT_VERSION', payload: null });
      setIsDirty(false);
      setSaveStatus('synced');
      setLastServerSync(Date.now());
      if (newDocument?.document) {
        dispatch({
          type: 'SET_SPEAKERS',
          payload: deriveSpeakers(newDocument)
        });
      }
    },
    [
      sessionId,
      state.documentResult,
      setIsDirty,
      setSaveStatus,
      setLastServerSync
    ]
  );

  // No-ops
  const reset = useCallback(() => {}, []);
  const handleMediaElementReady = useCallback(() => {}, []);
  const finishDocument = useCallback(
    (
      _sessionId: string,
      _documentId: string,
      _newStatus: boolean,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      _setFinished: (status: boolean, error?: any) => void
    ) => {},
    []
  );

  // #2237: report this pane's dirty state up through SplitDirty so
  // the shell's "any dirty" check (usePrompt + language-switch
  // save-first) covers both panes without an imperative handle.
  useReportDirty(documentId, state.isDirty ?? false);

  // Autosave + Cmd/Ctrl+S are now owned by this provider. Previously
  // the shell drove them via `translationHandleRef`; moving them in
  // here keeps "one dirty flag per open document, in one place".
  const autoSaveRef = useRef(silentSave);
  autoSaveRef.current = silentSave;
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firstDirtyRef = useRef<number>(0);
  useEffect(() => {
    if (!state.isDirty) {
      firstDirtyRef.current = 0;
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      return;
    }
    const now = Date.now();
    if (firstDirtyRef.current === 0) firstDirtyRef.current = now;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    const elapsed = now - firstDirtyRef.current;
    const delay = Math.min(30_000, Math.max(0, 120_000 - elapsed));
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveRef.current();
      firstDirtyRef.current = 0;
    }, delay);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [state.isDirty, state.document]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        saveDocument(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [saveDocument]);

  // ---- Context value ----

  // --- Conflict resolution (DraftConflictModal) -----------------------------
  // Apply the local draft over the server baseline and continue editing it.
  const resolveConflictKeepLocal = useCallback(() => {
    if (!conflict) return;
    dispatch({ type: 'SET_DOCUMENT', payload: conflict.localDocument });
    dispatch({ type: 'SET_UNDO_STACK', payload: conflict.localUndo });
    dispatch({ type: 'SET_REDO_STACK', payload: conflict.localRedo });
    documentRef.current = conflict.localDocument;
    undoStackRef.current = conflict.localUndo;
    redoStackRef.current = conflict.localRedo;
    // Prevent the API document from overwriting the recovered local draft.
    hasRecoveredData.current = true;
    setSaveStatus('saved-locally');
    setLastLocalSave(conflict.localTimestamp || Date.now());
    setConflict(null);
  }, [conflict, setSaveStatus, setLastLocalSave]);

  // Discard the local draft and keep the server copy (already the live state).
  const resolveConflictLoadCloud = useCallback(() => {
    if (sessionId && state.documentResult) {
      clearTranscriptFromLocalStorage(
        sessionId,
        state.documentResult.result.id
      );
    }
    setSaveStatus('synced');
    setConflict(null);
  }, [sessionId, state.documentResult, setSaveStatus]);

  const contextValue: TranscriptContextType = {
    ...state,
    setSearch,
    searchInputRef,
    setCaseSensitive,
    setWholeWord,
    setCurrentMatch,
    setStartStopWord,
    setPopoverOpen,
    setPopoverPosition,
    setShowInfo,
    setClickedComment,
    setShowJumpButton,
    saveDocument,
    saveDocumentLoading,
    documentResetting: false,
    finishedLoading: false,
    reset,
    handleMediaElementReady,
    applyStyle,
    addSpeaker,
    setEditor,
    updateDocument,
    autoSave: silentSave,
    finishDocument,
    addUtterance,
    deleteUtterance,
    currentEditorRef,
    splitUtterance,
    replaceSpeakers,
    editorUpdateParagraph,
    matches: state.matches,
    setMatches,
    focusMatch,
    undo,
    redo,
    updateDocumentResult,
    reloadFromServer,
    selectedParagraphs: state.selectedParagraphs,
    setSelectedParagraphs,
    toggleParagraphSelection,
    clearParagraphSelection,
    mergeUtterances
  };

  return (
    <TranscriptContext.Provider value={contextValue}>
      {conflict ? (
        <DraftConflictModal
          isOpen
          localSavedAt={conflict.localTimestamp}
          serverSavedAt={conflict.serverTimestamp}
          localCount={conflict.localCount}
          serverCount={conflict.serverCount}
          unit={t('transcript-segments-unit', 'segments')}
          onKeepLocal={resolveConflictKeepLocal}
          onLoadCloud={resolveConflictLoadCloud}
          onViewCloud={openCloudVersionInNewTab}
        />
      ) : (
        children
      )}
    </TranscriptContext.Provider>
  );
}
